import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, Eye, Filter, X, ChevronDown, ChevronUp, Users, Upload, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getCustomers, deleteCustomer, downloadExport } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import CustomerCSVUpload from '../../components/CustomerCSVUpload';
import { useTableSort, SortTh } from '../../components/ui/tableSort';

// Column -> value accessor for client-side sorting (module-level = stable).
const CUSTOMER_SORTS = {
  name: (c) => c.name,
  type: (c) => c.type,
  level: (c) => (c.level ?? 99),
  territory: (c) => c.territory,
  status: (c) => (c.isActive ? 1 : 0),
  compliance: (c) => (c.complianceScore == null ? null : Number(c.complianceScore)),
};

const Customers = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterTerritory, setFilterTerritory] = useState('ALL');
  const [filterComplianceScore, setFilterComplianceScore] = useState('ALL');
  const [filterEDIEnabled, setFilterEDIEnabled] = useState('ALL');
  const [filterRiskLevel, setFilterRiskLevel] = useState('ALL');
  const [filterContractGroup, setFilterContractGroup] = useState('ALL');
  const [filterClassOfTrade, setFilterClassOfTrade] = useState('ALL');
  const [filterSpecialty, setFilterSpecialty] = useState('ALL');
  const [filterTier, setFilterTier] = useState('ALL');
  
  // Advanced filter toggle
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // CSV Upload modal
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  
  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterType('ALL');
    setFilterStatus('ALL');
    setFilterEntityType('ALL');
    setFilterTerritory('ALL');
    setFilterComplianceScore('ALL');
    setFilterEDIEnabled('ALL');
    setFilterRiskLevel('ALL');
    setFilterContractGroup('ALL');
    setFilterClassOfTrade('ALL');
    setFilterSpecialty('ALL');
    setFilterTier('ALL');
  };

  // Export customers to Excel
  const exportToExcel = async () => {
    try {
      await downloadExport('/api/customers/export', `customers-export-${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Customers exported to Excel successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export customers to Excel');
    }
  };

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['customers'],
    queryFn: getCustomers,
    enabled: !!user,
    retry: false
  });

  // Extract customers array from response and ensure it's always an array
  const customers = Array.isArray(response?.customers) ? response.customers : 
                   Array.isArray(response) ? response : [];

  const deleteMutation = useMutation({
    mutationFn: deleteCustomer,
    onSuccess: () => {
      toast.success('Customer deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error deleting customer');
    }
  });

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      deleteMutation.mutate(id);
    }
  };

  // Get unique values for filter options
  const filterOptions = useMemo(() => {
    const options = {
      territories: [...new Set(customers.map(c => c.territory).filter(Boolean))],
      contractGroups: [...new Set(customers.map(c => c.contractGroup).filter(Boolean))],
      classOfTrades: [...new Set(customers.map(c => c.classOfTrade).filter(Boolean))],
      specialties: [...new Set(customers.map(c => c.specialty).filter(Boolean))],
      tiers: [...new Set(customers.map(c => c.tier).filter(Boolean))],
      complianceScores: [...new Set(customers.map(c => c.complianceScore).filter(Boolean))].sort((a, b) => a - b)
    };
    return options;
  }, [customers]);

  // Apply all filters
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      // Search filter
      const matchesSearch = !searchTerm || 
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.legalName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.ediEntityId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.npiNumber?.includes(searchTerm) ||
        customer.taxId?.includes(searchTerm);
      
      // Basic filters
      const matchesType = filterType === 'ALL' || customer.type === filterType;
      const matchesStatus = filterStatus === 'ALL' || 
        (filterStatus === 'ACTIVE' && customer.isActive) ||
        (filterStatus === 'INACTIVE' && !customer.isActive) ||
        (filterStatus === 'SUSPENDED' && !customer.isActive);
      const matchesTerritory = filterTerritory === 'ALL' || customer.territory === filterTerritory;
      
      // Advanced filters
      const matchesComplianceScore = filterComplianceScore === 'ALL' || 
        (customer.complianceScore && customer.complianceScore >= parseInt(filterComplianceScore));
      
      const matchesEDIEnabled = filterEDIEnabled === 'ALL' || 
        (filterEDIEnabled === 'YES' && customer.edi844Enabled) ||
        (filterEDIEnabled === 'NO' && !customer.edi844Enabled);
      
      const matchesRiskLevel = filterRiskLevel === 'ALL' || customer.riskLevel === filterRiskLevel;
      const matchesContractGroup = filterContractGroup === 'ALL' || customer.contractGroup === filterContractGroup;
      const matchesClassOfTrade = filterClassOfTrade === 'ALL' || customer.classOfTrade === filterClassOfTrade;
      const matchesSpecialty = filterSpecialty === 'ALL' || customer.specialty === filterSpecialty;
      const matchesTier = filterTier === 'ALL' || customer.tier === filterTier;
      
      return matchesSearch && matchesType && matchesStatus && 
             matchesTerritory && matchesComplianceScore && matchesEDIEnabled && 
             matchesRiskLevel && matchesContractGroup && matchesClassOfTrade && 
             matchesSpecialty && matchesTier;
    });
  }, [customers, searchTerm, filterType, filterStatus, filterTerritory, 
      filterComplianceScore, filterEDIEnabled, filterRiskLevel, filterContractGroup, 
      filterClassOfTrade, filterSpecialty, filterTier]);

  // Client-side column sorting over the filtered set.
  const { sorted: sortedCustomers, sort, toggleSort } = useTableSort(filteredCustomers, CUSTOMER_SORTS);

  // Count active filters
  const activeFilterCount = [
    searchTerm, filterType, filterStatus, filterTerritory,
    filterComplianceScore, filterEDIEnabled, filterRiskLevel, filterContractGroup,
    filterClassOfTrade, filterSpecialty, filterTier
  ].filter(filter => filter !== 'ALL' && filter !== '').length;

  // Redirect to login if not authenticated
  if (!user) {
    navigate('/app/login');
    return null;
  }

  if (isLoading) return <div className="flex justify-center p-8">Loading customers...</div>;
  if (error) return <div className="text-red-500 p-8">Error loading customers: {error.message}</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
        <div className="flex gap-3">
          <button
            onClick={exportToExcel}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
            title="Export Excel"
          >
            <Download size={20} />
            Export Excel
          </button>
          <button
            onClick={() => setShowCSVUpload(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
            title="Upload CSV"
          >
            <Upload size={20} />
            Upload CSV
          </button>
          <button
            onClick={() => navigate('/app/customers/new')}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-700"
            title="Add Customer"
          >
            <Plus size={20} />
            Add Customer
          </button>
        </div>
      </div>

      {/* Customer Analytics Summary */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-blue-500 text-white">
                  <Users className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Customers</dt>
                  <dd className="text-lg font-medium text-gray-900">{customers.length.toLocaleString()}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-green-500 text-white">
                  <Filter className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Wholesale</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {customers.filter(c => c.type === 'WHOLESALE').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-purple-500 text-white">
                  <Eye className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Direct</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {customers.filter(c => c.type === 'DIRECT').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-orange-500 text-white">
                  <Search className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">EDI Enabled</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {customers.filter(c => c.edi845Enabled).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filters */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Filter size={20} />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-primary-100 text-primary-800 text-xs font-medium px-2 py-1 rounded-full">
                {activeFilterCount} active
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
              title="Toggle advanced filters"
            >
              {showAdvancedFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {showAdvancedFilters ? 'Hide' : 'Show'} Advanced
            </button>
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                title="Clear all filters"
              >
                <X size={16} />
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* Basic Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, legal name, EDI ID, NPI, or Tax ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          
          {/* Customer Type */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="ALL">All Types</option>
            <option value="DIRECT">Direct</option>
            <option value="WHOLESALE">Wholesale</option>
          </select>
          
          {/* Status */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="SUSPENDED">Suspended</option>
          </select>
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            {/* Territory */}
            <select
              value={filterTerritory}
              onChange={(e) => setFilterTerritory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="ALL">All Territories</option>
              {filterOptions.territories.map(territory => (
                <option key={territory} value={territory}>{territory}</option>
              ))}
            </select>

            {/* Compliance Score */}
            <select
              value={filterComplianceScore}
              onChange={(e) => setFilterComplianceScore(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="ALL">All Compliance Scores</option>
              <option value="90">90+ (Excellent)</option>
              <option value="80">80+ (Good)</option>
              <option value="70">70+ (Fair)</option>
              <option value="60">60+ (Poor)</option>
            </select>

            {/* EDI Enabled */}
            <select
              value={filterEDIEnabled}
              onChange={(e) => setFilterEDIEnabled(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="ALL">All EDI Status</option>
              <option value="YES">EDI Enabled</option>
              <option value="NO">EDI Disabled</option>
            </select>

            {/* Risk Level */}
            <select
              value={filterRiskLevel}
              onChange={(e) => setFilterRiskLevel(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="ALL">All Risk Levels</option>
              <option value="LOW">Low Risk</option>
              <option value="MEDIUM">Medium Risk</option>
              <option value="HIGH">High Risk</option>
            </select>

            {/* Contract Group */}
            <select
              value={filterContractGroup}
              onChange={(e) => setFilterContractGroup(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="ALL">All Contract Groups</option>
              {filterOptions.contractGroups.map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>

            {/* Class of Trade */}
            <select
              value={filterClassOfTrade}
              onChange={(e) => setFilterClassOfTrade(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="ALL">All Classes of Trade</option>
              {filterOptions.classOfTrades.map(cot => (
                <option key={cot} value={cot}>{cot}</option>
              ))}
            </select>

            {/* Specialty */}
            <select
              value={filterSpecialty}
              onChange={(e) => setFilterSpecialty(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="ALL">All Specialties</option>
              {filterOptions.specialties.map(specialty => (
                <option key={specialty} value={specialty}>{specialty}</option>
              ))}
            </select>

            {/* Tier */}
            <select
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="ALL">All Tiers</option>
              {filterOptions.tiers.map(tier => (
                <option key={tier} value={tier}>{tier}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mb-4 text-sm text-gray-600">
        Showing {filteredCustomers.length} of {customers.length} customers
        {activeFilterCount > 0 && ` (${activeFilterCount} filters applied)`}
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortTh label="Customer" sortKey="name" sort={sort} onSort={toggleSort} />
                <SortTh label="Type" sortKey="type" sort={sort} onSort={toggleSort} />
                <SortTh label="Level" sortKey="level" sort={sort} onSort={toggleSort} />
                <SortTh label="Territory" sortKey="territory" sort={sort} onSort={toggleSort} />
                <SortTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
                <SortTh label="Compliance" sortKey="compliance" sort={sort} onSort={toggleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(sortedCustomers) && sortedCustomers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                      <div className="text-sm text-gray-500">{customer.legalName}</div>
                      <div className="text-xs text-gray-400">{customer.ediEntityId}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        customer.type === 'DIRECT' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {customer.type}
                      </span>
                      {customer.classOfTrade && (
                        <span className="text-xs text-gray-500">{customer.classOfTrade}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        customer.level === 0 ? 'bg-purple-100 text-purple-800' :
                        customer.level === 1 ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {customer.level === 0 ? 'Parent' : customer.level === 1 ? 'Child' : 'Grandchild'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col">
                      <span>{customer.territory || '-'}</span>
                      {customer.contractGroup && (
                        <span className="text-xs text-gray-500">{customer.contractGroup}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      customer.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {customer.isActive ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col gap-1">
                      {customer.complianceScore ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          customer.complianceScore >= 90 ? 'bg-green-100 text-green-800' :
                          customer.complianceScore >= 80 ? 'bg-blue-100 text-blue-800' :
                          customer.complianceScore >= 70 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {customer.complianceScore}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                      {customer.riskLevel && (
                        <span className={`text-xs px-1 py-0.5 rounded ${
                          customer.riskLevel === 'LOW' ? 'bg-green-50 text-green-700' :
                          customer.riskLevel === 'MEDIUM' ? 'bg-yellow-50 text-yellow-700' :
                          'bg-red-50 text-red-700'
                        }`}>
                          {customer.riskLevel}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/app/customers/${customer.id}`)}
                        className="text-primary-600 hover:text-primary-900"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => navigate(`/app/customers/${customer.id}/edit`)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(customer.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredCustomers?.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Filter size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium text-gray-900 mb-2">No customers found</p>
            <p className="text-gray-500">
              {activeFilterCount > 0 
                ? "Try adjusting your filters or search terms."
                : "No customers match your current criteria."
              }
            </p>
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="mt-4 text-primary-600 hover:text-primary-800 font-medium"
                title="Clear all filters"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* CSV Upload Modal */}
      {showCSVUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <CustomerCSVUpload
            onUploadComplete={(result) => {
              setShowCSVUpload(false);
              queryClient.invalidateQueries({ queryKey: ['customers'] });
              toast.success(`CSV upload completed! ${result.summary.successCount} customers processed.`);
            }}
            onClose={() => setShowCSVUpload(false)}
          />
        </div>
      )}
    </div>
  );
};

export default Customers;
